import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function QaSubprojectDetail() {
  const navigate = useNavigate();
  const { projectId, subprojectId } = useParams();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [project, setProject] = useState(null);
  const [subproject, setSubproject] = useState(null);
  const [ssps, setSsps] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => { if (companyId && projectId && subprojectId) fetchData(); }, [companyId, projectId, subprojectId]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchData = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/companies/${companyId}/qa/projects/${projectId}/subprojects/${subprojectId}`);
    if (res && res.ok) {
      const data = await res.json();
      setProject(data.project || null);
      setSubproject(data.subproject || null);
      setSsps(Array.isArray(data.sub_subprojects) ? data.sub_subprojects : []);
    }
    setLoading(false);
  };

  const createSSP = async () => {
    if (!newName.trim()) return;
    const res = await authFetch(`${API}/companies/${companyId}/qa/projects/${projectId}/subprojects/${subprojectId}/sub-subprojects`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newName })
    });
    if (res && res.ok) { setShowCreate(false); setNewName(''); fetchData(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const renameSSP = async (sspId) => {
    if (!editName.trim()) return;
    const res = await authFetch(`${API}/companies/${companyId}/qa/projects/${projectId}/subprojects/${subprojectId}/sub-subprojects/${sspId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: editName })
    });
    if (res && res.ok) { setEditingId(null); fetchData(); }
  };

  const toggleStatus = async (sspId, current) => {
    const newStatus = current === 'solved' ? 'unsolved' : 'solved';
    await authFetch(`${API}/companies/${companyId}/qa/projects/${projectId}/subprojects/${subprojectId}/sub-subprojects/${sspId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus })
    });
    fetchData();
  };

  const archiveSSP = async (sspId) => {
    if (!window.confirm('Archive this sub-subproject?')) return;
    await authFetch(`${API}/companies/${companyId}/qa/projects/${projectId}/subprojects/${subprojectId}/sub-subprojects/${sspId}/archive`, { method: 'POST' });
    fetchData();
  };

  if (!companyId) return <div style={s.page}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={s.page}><p>Loading...</p></div>;
  if (!subproject) return <div style={s.page}><p>Subproject not found.</p></div>;

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>
        <span style={s.breadLink} onClick={() => navigate('/company/qa')}>QA Management</span>
        <span style={s.breadSep}>/</span>
        <span style={s.breadLink} onClick={() => navigate(`/company/qa/projects/${projectId}`)}>{project?.name}</span>
        <span style={s.breadSep}>/</span>
        <span>{subproject.name}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={s.title}>{subproject.name}</h2>
        <button onClick={() => setShowCreate(true)} style={s.btnPrimary}>+ New Sub-subproject</button>
      </div>

      {ssps.length === 0 && <p style={{ color: '#999' }}>No sub-subprojects yet.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ssps.map(ssp => (
          <div key={ssp.id} style={{ ...s.listCard, borderLeft: `4px solid ${ssp.status === 'solved' ? '#27ae60' : '#e74c3c'}` }}>
            <div style={{ flex: 1, cursor: 'pointer' }}
                 onClick={() => navigate(`/company/qa/projects/${projectId}/subprojects/${subprojectId}/workspace/${ssp.id}`)}>
              {editingId === ssp.id ? (
                <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                  <input value={editName} onChange={e => setEditName(e.target.value)} style={{ ...s.input, marginBottom: 0, flex: 1 }}
                         onKeyDown={e => e.key === 'Enter' && renameSSP(ssp.id)} autoFocus />
                  <button onClick={() => renameSSP(ssp.id)} style={s.btnSm}>Save</button>
                  <button onClick={() => setEditingId(null)} style={{ ...s.btnSm, color: '#999' }}>Cancel</button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{ssp.name}</h3>
                    <span style={{ ...s.badge, backgroundColor: ssp.status === 'solved' ? '#27ae60' : '#e74c3c', fontSize: 10 }}>
                      {ssp.status.toUpperCase()}
                    </span>
                  </div>
                  <div style={s.counters}>
                    <span style={{ color: '#e74c3c' }}>{ssp.unsolved_count} Unsolved</span>
                    <span style={{ color: '#27ae60' }}>{ssp.solved_count} Solved</span>
                    <span style={{ color: '#8e44ad' }}>{ssp.critical_count} Critical</span>
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
              <button onClick={() => toggleStatus(ssp.id, ssp.status)} style={s.btnSm}>{ssp.status === 'solved' ? 'Reopen' : 'Solve'}</button>
              <button onClick={() => { setEditingId(ssp.id); setEditName(ssp.name); }} style={s.btnSm}>Rename</button>
              <button onClick={() => archiveSSP(ssp.id)} style={{ ...s.btnSm, color: '#e74c3c' }}>Archive</button>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>New Sub-subproject</h3>
            <label style={s.label}>Name *</label>
            <input value={newName} onChange={e => setNewName(e.target.value)} style={s.input} placeholder="e.g. Withdraw Design"
                   onKeyDown={e => e.key === 'Enter' && createSSP()} autoFocus />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={createSSP} style={s.btnPrimary}>Create</button>
              <button onClick={() => setShowCreate(false)} style={s.btnCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  breadcrumb: { fontSize: 13, color: '#888', marginBottom: 12 },
  breadLink: { color: '#3498db', cursor: 'pointer' },
  breadSep: { margin: '0 6px' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, color: '#fff', fontSize: 11, fontWeight: 700 },
  counters: { display: 'flex', gap: 14, fontSize: 12, marginTop: 4, color: '#666' },
  listCard: { background: '#fff', borderRadius: 8, padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 },
  btnPrimary: { padding: '8px 18px', background: '#3498db', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnCancel: { padding: '8px 18px', background: '#ecf0f1', color: '#2c3e50', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  btnSm: { padding: '4px 10px', background: '#ecf0f1', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 450 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 },
  input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%', boxSizing: 'border-box', marginBottom: 8 },
};